import type {
  FunctionsHttpErrorLike,
  NormalizedSupabaseError,
  PostgrestErrorLike,
  SupabaseAuthErrorLike,
  SupabaseHttpLike,
  UnknownRecord,
} from "@/types";

export function isSupabaseAuthError(
  err: unknown,
): err is SupabaseAuthErrorLike {
  return Boolean(err && (err as { name?: string }).name === "AuthApiError");
}

export function isPostgrestError(err: unknown): err is PostgrestErrorLike {
  const name = (err as { name?: string })?.name;
  if (name === "PostgrestError") return true;
  const e = err as { code?: unknown; message?: unknown };
  return typeof e?.code === "string" && typeof e?.message === "string";
}

export function isFunctionsHttpError(
  err: unknown,
): err is FunctionsHttpErrorLike {
  return Boolean(
    err && (err as { name?: string }).name === "FunctionsHttpError",
  );
}

export function isSupabaseErrorLike(err: unknown): boolean {
  return (
    isSupabaseAuthError(err) ||
    isPostgrestError(err) ||
    isFunctionsHttpError(err)
  );
}

export function getHttpStatusFromSupabase(err: unknown): number | undefined {
  const e = err as SupabaseHttpLike & { status?: unknown };
  const status = (e?.response?.status ??
    (e as { status?: unknown })?.status) as unknown;
  return typeof status === "number" ? status : undefined;
}

export function normalizeSupabaseError(err: unknown): NormalizedSupabaseError {
  const out: NormalizedSupabaseError = {};
  if (isSupabaseAuthError(err)) {
    out.status = getHttpStatusFromSupabase(err);
    out.code = typeof err.code === "string" ? err.code : undefined;
  } else if (isPostgrestError(err)) {
    out.code =
      typeof (err as { code?: unknown }).code === "string"
        ? (err as { code?: string }).code
        : undefined;
    out.details = pickDetails(err, ["message", "details", "hint"]);
  } else if (isFunctionsHttpError(err)) {
    out.status = getHttpStatusFromSupabase(err);
    const details = pickDetails(err, ["context", "message"]);
    const ctxCode = (details?.context as { code?: unknown } | undefined)?.code;
    if (typeof ctxCode === "string") out.code = ctxCode;
    out.details = details;
  }

  return out;
}

function pickDetails(err: unknown, keys: string[]): UnknownRecord | undefined {
  const e = err as UnknownRecord;
  const out: UnknownRecord = {};
  for (const k of keys) {
    if (e && k in e) out[k] = e[k];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
